import React, { useEffect, useState } from 'react';
import axiosInstance from '../../config/axios';
import { useNavigate } from 'react-router-dom';
import partnerService from '../../services/api/larkdata.service';
import authService from '../../services/api/auth.service';

const VerifyEmail: React.FC = () => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [otpLoading, setOtpLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [partnerDataPosted, setPartnerDataPosted] = useState(false);
  const [userId, setUserId] = useState('');
  const [isEditingEmail, setIsEditingEmail] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      setInitialLoading(true);
      try {
        const profileData = await authService.getProfile();
        const user = profileData.data;

        setEmail(user.email || '');
        setUserId(user.id || user._id || '');

        const [emailVerificationResult, partnerDataResult] = await Promise.allSettled([
          Promise.resolve(user.email && user.is_email_verified === true),
          partnerService.fetchPartnerProfile(`${user.phone_no}`)
        ]);

        const isEmailVerified = emailVerificationResult.status === 'fulfilled' && emailVerificationResult.value === true;

        if (isEmailVerified) {
          setVerified(true);
        } else {
          setVerified(false);
        }

        if (partnerDataResult.status === 'fulfilled') {
          setPartnerDataPosted(true);
        } else {
          console.error('PartnerService DB sync failed:', partnerDataResult.reason);
          setPartnerDataPosted(true);
        }

      } catch (err) {
        console.error('Profile fetch failed:', err);
        setPartnerDataPosted(true);
      } finally {
        setInitialLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (verified && partnerDataPosted) {
      const timer = setTimeout(() => {
        navigate('/loan/apply');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [verified, partnerDataPosted, navigate]);

  const updateApplicationStatus = async () => {
    if (!userId) return;
    try {
      await partnerService.updateApplicationStatus(userId, 'EMAIL_VERIFIED');
    } catch (err) {
      console.error('Failed to update application status:', err);
    }
  };

  const updateEmailVerificationStatus = async () => {
    if (!userId) return;
    try {
      await axiosInstance.put(`/users/${userId}`, {
        email: email,
        is_email_verified: true
      });
    } catch (err) {
      console.error('Failed to update email verification status:', err);
    }
  };

  const sendOtp = async () => {
    setError('');
    setMessage('');
    setOtpLoading(true);
    try {
      const response = await axiosInstance.post('/email/send-otp', { email });
      if (response?.data?.message === 'Email already verified.') {
        setVerified(true);
        setShowOtpInput(false);
        await updateApplicationStatus();
        return;
      }
      setShowOtpInput(true);
      setIsEditingEmail(false);
      setMessage('OTP sent to your email.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send OTP.');
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    setError('');
    setMessage('');
    setOtpLoading(true);
    try {
      await axiosInstance.post('/email/verify-otp', { email, otp });
      setVerified(true);
      setShowOtpInput(false);
      setMessage('Email verified successfully. Redirecting...');
      await updateApplicationStatus();
      await updateEmailVerificationStatus();
    } catch (err: any) {
      setError(err.response?.data?.message || 'OTP verification failed.');
    } finally {
      setOtpLoading(false);
    }
  };

  const resendOtp = async () => {
    setError('');
    setMessage('');
    setOtpLoading(true);
    try {
      await axiosInstance.post('/email/send-otp', { email });
      setMessage('OTP resent to your email.');
    } catch {
      setError('Failed to resend OTP.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleEditEmail = () => {
    setIsEditingEmail(true);
    setShowOtpInput(false);
    setOtp('');
    setMessage('');
    setError('');
  };

  const LoadingContent = ({ message }: { message: string }) => (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700 mb-4"></div>
      <p className="text-gray-700 text-lg font-medium">{message}</p>
    </div>
  );

  if (initialLoading) {
    return (
      <div className="w-full max-w-[calc(100vw-2rem)] md:max-w-[500px] lg:max-w-[500px] h-auto min-h-[300px] md:h-[500px] mt-10 mx-4 p-4 md:p-6 bg-white rounded-2xl shadow-md">
        <LoadingContent message="Please wait..." />
      </div>
    );
  }

  if (verified && partnerDataPosted) {
    return (
      <div className="w-full max-w-[calc(100vw-2rem)] md:max-w-[500px] lg:max-w-[500px] h-auto min-h-[300px] md:h-[500px] mt-10 mx-4 p-4 md:p-6 bg-white rounded-2xl shadow-md">
        <LoadingContent message="Email verified successfully. Redirecting to loan application..." />
      </div>
    );
  }

  return (
    <div className="w-full max-w-[calc(100vw-2rem)] md:max-w-[500px] lg:max-w-[500px] h-auto min-h-[300px] md:h-[500px] mt-10 mx-4 p-4 md:p-6 bg-white rounded-2xl shadow-md relative">
      {otpLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-2xl z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700 mx-auto mb-4"></div>
            <p className="text-gray-700 text-lg font-medium">Processing...</p>
          </div>
        </div>
      )}
      
      <div className="h-full overflow-y-auto">
        <h2 className="text-2xl font-bold text-green-800 mb-6">Email Verification</h2>
        <div className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Enter Your Email Address
            </label>
            <div className="relative">
              <input
                type="email"
                id="email"
                value={email}
                disabled={showOtpInput && !isEditingEmail}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full rounded-md border border-green-600 bg-gray-100 p-3 focus:ring-green-700 focus:border-green-700 ${
                  (showOtpInput && !isEditingEmail) ? 'opacity-60' : ''
                }`}
                placeholder="you@example.com"
              />
              {showOtpInput && !isEditingEmail && (
                <button
                  type="button"
                  onClick={handleEditEmail}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-green-700 hover:text-green-800 font-medium text-sm px-2 py-1 rounded"
                >
                  Edit
                </button>
              )}
            </div>
          </div>

          {(!showOtpInput || isEditingEmail) && (
            <button
              type="button"
              onClick={sendOtp}
              disabled={!email || otpLoading}
              className="w-full bg-green-700 text-white py-3 px-4 rounded-xl hover:bg-green-800 transition font-medium"
            >
              {otpLoading ? 'Sending...' : 'Send OTP'}
            </button>
          )}

          {showOtpInput && !isEditingEmail && (
            <div className="space-y-4">
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                  Enter OTP
                </label>
                <input
                  type="text"
                  id="otp"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="w-full rounded-md border border-green-600 bg-gray-100 p-3 focus:ring-green-700 focus:border-green-700"
                  placeholder="6-digit code"
                  maxLength={6}
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={verifyOtp}
                  disabled={!otp || otpLoading}
                  className="bg-green-700 text-white py-3 px-6 rounded-xl hover:bg-green-800 transition font-medium"
                >
                  {otpLoading ? 'Verifying...' : 'Verify OTP'}
                </button>
                <button
                  type="button"
                  onClick={resendOtp}
                  disabled={otpLoading}
                  className="text-green-700 hover:underline font-medium py-2"
                >
                  Resend OTP
                </button>
              </div>
            </div>
          )}

          {message && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-700 font-medium">{message}</p>
            </div>
          )}
          
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 font-medium">{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;

